import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../utils/type';
import {
  selectVendorPayments,
  selectVendorPaymentsLoading,
  selectVendorPaymentsError,
  selectVendorPaymentsDownloadError,
} from '../services/vendorPayments/selectors';
import {
  getVendorPayments,
  downloadVendorPayments,
} from '../services/vendorPayments/crudFunctions';
import { VendorPaymentFilters } from '../model/reports';
import LoadingSpinner from '../../../elements/LoadingSpinner';
import VendorSelect from '../../../elements/VendorSelect';
import Pagination from '../../../elements/Pagination';

const PAGE_SIZE = 10;

const VendorPaymentReport: React.FC = () => {
  const dispatch = useDispatch();
  const data = useSelector((s: RootState) => selectVendorPayments(s));
  const loading = useSelector((s: RootState) => selectVendorPaymentsLoading(s));
  const error = useSelector((s: RootState) => selectVendorPaymentsError(s));
  const downloadError = useSelector((s: RootState) => selectVendorPaymentsDownloadError(s));

  const [filters, setFilters] = useState<VendorPaymentFilters>({});
  const [format, setFormat] = useState<'csv' | 'xls'>('csv');

  // Pagination state — separate for vendor summaries and records tables
  const [summaryPage, setSummaryPage] = useState(1);
  const [summaryPageSize, setSummaryPageSize] = useState(PAGE_SIZE);
  const [recordsPage, setRecordsPage] = useState(1);
  const [recordsPageSize, setRecordsPageSize] = useState(PAGE_SIZE);

  useEffect(() => {
    dispatch<any>(getVendorPayments({}));
  }, [dispatch]);

  // Reset to page 1 when data changes
  useEffect(() => {
    setSummaryPage(1);
    setRecordsPage(1);
  }, [data]);

  const handleFilter = (e: React.FormEvent) => {
    e.preventDefault();
    dispatch<any>(getVendorPayments(filters));
  };

  const handleDownload = () => {
    dispatch<any>(downloadVendorPayments({ ...filters, format }));
  };

  const fmt = (n?: number | string) => {
    const num = Number(n);
    return !isNaN(num) ? `₹${num.toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : '—';
  };

  const fmtDate = (s?: string) =>
    s ? new Date(s).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '—';

  // Paginated slices
  const vendorSummaries = data?.vendorSummaries ?? [];
  const records = data?.records ?? [];

  const pagedSummaries = vendorSummaries.slice(
    (summaryPage - 1) * summaryPageSize,
    summaryPage * summaryPageSize
  );
  const pagedRecords = records.slice(
    (recordsPage - 1) * recordsPageSize,
    recordsPage * recordsPageSize
  );

  return (
    <div>
      <div className="page-title">
        <div className="title_left">
          <h3>
            <i className="bi bi-currency-rupee me-2 text-success" />
            Vendor Payment Report
          </h3>
        </div>
      </div>

      {/* Filters */}
      <div className="x_panel">
        <div className="x_title">
          <h4>Filters</h4>
          <div className="clearfix" />
        </div>
        <div className="x_content">
          {(error || downloadError) && (
            <div className="alert alert-danger">
              <i className="fas fa-exclamation-triangle me-2" />
              {error || downloadError}
            </div>
          )}
          <form onSubmit={handleFilter}>
            <div className="row g-3 mb-3">
              <div className="col-md-3">
                <label className="form-label">Start Date</label>
                <input
                  type="date"
                  className="form-control"
                  value={filters.startDate || ''}
                  onChange={(e) =>
                    setFilters((f) => ({ ...f, startDate: e.target.value || undefined }))
                  }
                />
              </div>
              <div className="col-md-3">
                <label className="form-label">End Date</label>
                <input
                  type="date"
                  className="form-control"
                  value={filters.endDate || ''}
                  onChange={(e) =>
                    setFilters((f) => ({ ...f, endDate: e.target.value || undefined }))
                  }
                />
              </div>
              <div className="col-md-3">
                <VendorSelect
                  value={filters.vendorId}
                  onChange={(id) => setFilters((f) => ({ ...f, vendorId: id }))}
                />
              </div>
              <div className="col-md-3">
                <label className="form-label">Download Format</label>
                <select
                  className="form-select"
                  value={format}
                  onChange={(e) => setFormat(e.target.value as 'csv' | 'xls')}
                >
                  <option value="csv">CSV</option>
                  <option value="xls">XLS</option>
                </select>
              </div>
            </div>
            <div className="row g-3">
              <div className="col-12 d-flex gap-2">
                <button type="submit" className="btn btn-success px-4">
                  <i className="fas fa-search me-2" /> Apply Filters
                </button>
                <button
                  type="button"
                  className="btn px-4"
                  onClick={handleDownload}
                  style={{ background: 'var(--gt-secondary)', color: '#fff', border: 'none' }}
                >
                  <i className="fas fa-download me-2" /> Download
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : (
        <>
          {/* Summary Cards */}
          {data?.summary && (
            <div className="row gy-3 mb-4">
              {[
                { label: 'Total Records', value: data.summary.totalRecords, icon: 'bi-list-ol', color: 'bg-primary' },
                { label: 'Total Amount', value: fmt(data.summary.totalAmount), icon: 'bi-currency-rupee', color: 'bg-success' },
                { label: 'Total GST', value: fmt(data.summary.totalGst), icon: 'bi-receipt', color: 'bg-info' },
                { label: 'Total Commission', value: fmt(data.summary.totalCommission), icon: 'bi-percent', color: 'bg-warning' },
                { label: 'Vendor Payout', value: fmt(data.summary.totalVendorPayout), icon: 'bi-wallet2', color: 'bg-danger' },
                {
                  label: 'Energy (kWh)',
                  value: `${Number(data.summary.totalEnergyConsumed)?.toFixed(2)} kWh`,
                  icon: 'bi-lightning-charge',
                  color: 'bg-secondary',
                },
              ].map((card) => (
                <div className="col-xl-2 col-md-4 col-sm-6" key={card.label}>
                  <div className="card h-100">
                    <div className="card-body">
                      <div className="d-flex align-items-center">
                        <div
                          className={`${card.color} text-white rounded-circle d-flex align-items-center justify-content-center me-3`}
                          style={{ width: 44, height: 44, flexShrink: 0 }}
                        >
                          <i className={`bi ${card.icon}`} />
                        </div>
                        <div>
                          <div className="text-muted small text-uppercase">{card.label}</div>
                          <div className="fw-bold">{card.value}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Vendor Summaries Table */}
          {vendorSummaries.length > 0 && (
            <div className="x_panel">
              <div className="x_title">
                <h4>Vendor Summaries</h4>
                <div className="clearfix" />
              </div>
              <div className="x_content">
                <div className="table-responsive">
                  <table className="table table-striped table-hover table-bordered">
                    <thead className="table-dark">
                      <tr>
                        <th>#</th>
                        <th>Vendor Name</th>
                        <th>Email</th>
                        <th>Mobile</th>
                        <th>Commission Rate</th>
                        <th>Sessions</th>
                        <th>Amount</th>
                        <th>GST</th>
                        <th>Commission</th>
                        <th>Payout</th>
                        <th>Energy (kWh)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pagedSummaries.map((v, idx) => (
                        <tr key={v.vendor_id}>
                          <td>{(summaryPage - 1) * summaryPageSize + idx + 1}</td>
                          <td>{v.vendor_name}</td>
                          <td>{v.vendor_email}</td>
                          <td>{v.vendor_mobile}</td>
                          <td>{(Number(v.vendor_commission_rate) * 100).toFixed(0)}%</td>
                          <td>{v.total_sessions}</td>
                          <td>{fmt(v.total_amount)}</td>
                          <td>{fmt(v.total_gst)}</td>
                          <td>{fmt(v.total_commission)}</td>
                          <td>{fmt(v.total_vendor_payout)}</td>
                          <td>{Number(v.total_energy_consumed)?.toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <Pagination
                  currentPage={summaryPage}
                  totalItems={vendorSummaries.length}
                  pageSize={summaryPageSize}
                  onPageChange={setSummaryPage}
                  onPageSizeChange={setSummaryPageSize}
                />
              </div>
            </div>
          )}

          {/* Transaction Records Table */}
          {records.length > 0 && (
            <div className="x_panel">
              <div className="x_title">
                <h4>Transaction Records</h4>
                <div className="clearfix" />
              </div>
              <div className="x_content">
                <div className="table-responsive">
                  <table className="table table-striped table-hover table-bordered">
                    <thead className="table-dark">
                      <tr>
                        <th>#</th>
                        <th>Earning ID</th>
                        <th>Vendor</th>
                        <th>Customer</th>
                        <th>Mobile</th>
                        <th>Station</th>
                        <th>Charger</th>
                        <th>Connector</th>
                        <th>Status</th>
                        <th>Start</th>
                        <th>End</th>
                        <th>Energy</th>
                        <th>Amount</th>
                        <th>GST</th>
                        <th>Final</th>
                        <th>Commission</th>
                        <th>Payout</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pagedRecords.map((r, idx) => (
                        <tr key={r.earning_id}>
                          <td>{(recordsPage - 1) * recordsPageSize + idx + 1}</td>
                          <td>{r.earning_id}</td>
                          <td>{r.vendor_name}</td>
                          <td>{r.customer_name}</td>
                          <td>{r.customer_mobile}</td>
                          <td>{r.station_name}</td>
                          <td>{r.charger_id}</td>
                          <td>{r.connector_id}</td>
                          <td>
                            <span
                              className={`badge ${
                                r.session_status === 'Completed'
                                  ? 'bg-success'
                                  : r.session_status === 'Failed'
                                  ? 'bg-danger'
                                  : r.session_status === 'Started'
                                  ? 'bg-primary'
                                  : 'bg-secondary'
                              }`}
                            >
                              {r.session_status}
                            </span>
                          </td>
                          <td>{fmtDate(r.start_time)}</td>
                          <td>{fmtDate(r.end_time)}</td>
                          <td>{Number(r.energy_consumed)?.toFixed(2)} kWh</td>
                          <td>{fmt(r.total_amount)}</td>
                          <td>{fmt(r.total_gst)}</td>
                          <td>{fmt(r.final_amount)}</td>
                          <td>{fmt(r.commission_amount)}</td>
                          <td>{fmt(r.vendor_payout)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <Pagination
                  currentPage={recordsPage}
                  totalItems={records.length}
                  pageSize={recordsPageSize}
                  onPageChange={setRecordsPage}
                  onPageSizeChange={setRecordsPageSize}
                />
              </div>
            </div>
          )}

          {data && !records.length && !loading && (
            <div className="x_panel">
              <div className="x_content text-center py-5 text-muted">
                <i className="bi bi-inbox fs-1 d-block mb-2" />
                No records found for the selected filters.
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default VendorPaymentReport;
